import React, { useId } from 'react';
import { BauteilEigenschaft } from '../types';
import { Icon } from './ui';

/**
 * Freie Schlüssel/Wert-Paare eines Bauteils. Jeder Schlüssel wird in der Elektroliste zur Tabellenspalte;
 * deshalb schlägt das Feld die schon benutzten Schlüssel vor, damit nicht „Leistung“ neben „El. Leistung“ entsteht.
 */
export const EigenschaftenEditor: React.FC<{
  eigenschaften: BauteilEigenschaft[];
  onChange: (eigenschaften: BauteilEigenschaft[]) => void;
  vorschlaege: string[];
}> = ({ eigenschaften, onChange, vorschlaege }) => {
  const listId = useId();

  const aendern = (index: number, feld: keyof BauteilEigenschaft, wert: string) =>
    onChange(eigenschaften.map((e, i) => (i === index ? { ...e, [feld]: wert } : e)));

  return (
    <div>
      <datalist id={listId}>
        {vorschlaege.map(v => <option key={v} value={v} />)}
      </datalist>
      {eigenschaften.length > 0 && (
        <div className="space-y-2 mb-2">
          {eigenschaften.map((e, i) => (
            <div key={i} className="flex gap-2">
              <input
                type="text"
                list={listId}
                value={e.schluessel}
                onChange={(ev) => aendern(i, 'schluessel', ev.target.value)}
                placeholder="z. B. Spannung"
                aria-label="Eigenschaft"
                className="w-2/5 min-w-0 p-2 border border-gray-300 rounded-lg text-sm"
              />
              <input
                type="text"
                value={e.wert}
                onChange={(ev) => aendern(i, 'wert', ev.target.value)}
                placeholder="z. B. 230 V"
                aria-label="Wert"
                className="flex-1 min-w-0 p-2 border border-gray-300 rounded-lg text-sm"
              />
              <button
                type="button"
                onClick={() => onChange(eigenschaften.filter((_, j) => j !== i))}
                className="flex-shrink-0 p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                aria-label="Eigenschaft entfernen"
                title="Eigenschaft entfernen"
              >
                <Icon.Close className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
      <button
        type="button"
        onClick={() => onChange([...eigenschaften, { schluessel: '', wert: '' }])}
        className="inline-flex items-center gap-1 text-sm text-brand-600 hover:text-brand-800"
      >
        <Icon.Plus className="w-4 h-4" />
        Eigenschaft hinzufügen
      </button>
    </div>
  );
};

/** Alle bisher benutzten Schlüssel, ohne Doppel durch Groß-/Kleinschreibung, die häufigsten zuerst. */
export const bekannteSchluessel = (listen: BauteilEigenschaft[][]): string[] => {
  const gesehen = new Map<string, { schluessel: string; anzahl: number }>();
  listen.flat().forEach(e => {
    const s = e.schluessel.trim();
    if (!s) return;
    const eintrag = gesehen.get(s.toLowerCase());
    if (eintrag) eintrag.anzahl++;
    else gesehen.set(s.toLowerCase(), { schluessel: s, anzahl: 1 });
  });
  // sort ist stabil: bei gleicher Häufigkeit bleibt die Reihenfolge des ersten Auftretens.
  return Array.from(gesehen.values()).sort((a, b) => b.anzahl - a.anzahl).map(e => e.schluessel);
};
